import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";
import TableContainer from "../../../components/Common/TableContainer";
import { OrdersTable } from "./OrdersTable";

const EcommerceOrders = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchById, setSearchById] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 30,
    totalOrders: 0,
    totalPages: 0,
    hasMore: false,
  });
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [updating, setUpdating] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [printTypeModal, setPrintTypeModal] = useState(false);
  const [selectedPrintOrder, setSelectedPrintOrder] = useState(null);
  const [selectedPrintType, setSelectedPrintType] = useState("");

  const calculateItemSubtotal = (price, discount, quantity) => {
    return price * quantity;
  };

  const calculateItemDiscount = (price, discount, quantity) => {
    return (price * discount * quantity) / 100;
  };

  useEffect(() => {
    let mounted = true;

    const loadOrders = async () => {
      try {
        if (!initialLoading) {
          setRefreshing(true);
        }

        setOrders([]);

        const params = {
          page: pagination.page,
          limit: pagination.limit,
          status:
            statusFilter === "outfordelivery" ? "outForDelivery" : statusFilter,
          shift: dateFilter,
        };

        // Add search parameters based on which search is being used
        if (searchById) {
          params.searchById = searchById;
        } else if (searchQuery) {
          params.search = searchQuery;
        }

        if (customDate) {
          params.date = customDate;
        }

        const response = await axiosInstance.get("/api/v1/admin/orders", {
          params,
        });

        if (mounted) {
          setOrders(response.data.orders || []);
          setPagination(response.data.pagination);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        if (mounted) {
          setOrders([]);
        }
      } finally {
        if (mounted) {
          setInitialLoading(false);
          setRefreshing(false);
        }
      }
    };

    loadOrders();

    return () => {
      mounted = false;
    };
  }, [
    pagination.page,
    searchQuery,
    searchById,
    statusFilter,
    dateFilter,
    customDate,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const statusFromUrl = queryParams.get("status");
    const dateFromUrl = queryParams.get("date");
    if (statusFromUrl) {
      setStatusFilter(statusFromUrl.toLowerCase());
    }
    if (dateFromUrl) {
      setDateFilter(dateFromUrl.toLowerCase());
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (statusFilter) {
      queryParams.set("status", statusFilter.toLowerCase());
    } else {
      queryParams.delete("status");
    }
    if (dateFilter) {
      queryParams.set("date", dateFilter.toLowerCase());
    } else {
      queryParams.delete("date");
    }
    const newUrl = `${window.location.pathname}${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    window.history.replaceState(null, "", newUrl);
  }, [statusFilter, dateFilter]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchById = (e) => {
    setSearchById(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearIdSearch = () => {
    setSearchById("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const getStatusColor = (status, rejectStatus) => {
    // First check for reject status if provided
    if (rejectStatus) {
      switch (rejectStatus.toLowerCase()) {
        case "resolved":
          return "success";
        case "pending":
          return "warning";
        default:
          return "secondary";
      }
    }

    // Then check main status
    switch (status?.toLowerCase()) {
      case "delivered":
      case "completed":
        return "success";
      case "processing":
      case "accepted":
      case "printed":
        return "info";
      case "cancelled":
      case "rejected":
        return "danger";
      case "pending":
        return "warning";
      case "outfordelivery":
        return "primary";
      case "paymentpending":
        return "warning";
      default:
        return "secondary";
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedOrders.length === 0) {
      return;
    }
    try {
      setUpdating(true);

      // Update order status
      await axiosInstance.put("/api/v1/admin/orders/update-status", {
        orders: selectedOrders.map((orderId) => ({
          orderId,
          status: bulkStatus,
        })),
      });

      // If status is rejected or cancelled, update the reject status
      if (bulkStatus === "rejected" || bulkStatus === "cancelled") {
        await axiosInstance.put("/api/v1/admin/orders/update-printed-status", {
          orders: selectedOrders.map((orderId) => ({
            orderId,
            rejectStatus: "pending",
          })),
        });
      }

      // Reload the page after successful update
      window.location.reload();
    } catch (error) {
      console.error("Error updating orders:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleSingleStatusUpdate = async (
    orderId,
    status,
    rejectStatus = ""
  ) => {
    try {
      setUpdating(true);

      // Update order status
      await axiosInstance.put("/api/v1/admin/orders/update-status", {
        orders: [
          {
            orderId,
            status,
          },
        ],
      });

      // If status is rejected or cancelled, update the reject status
      if (
        status === "rejected" ||
        status === "cancelled" ||
        status === "paymentpending"
      ) {
        await axiosInstance.put("/api/v1/admin/orders/update-printed-status", {
          orders: [
            {
              orderId,
              rejectStatus,
            },
          ],
        });
      }

      // Reload the page after successful update
      window.location.reload();
    } catch (error) {
      console.error("Error updating order status:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handlePrintOrders = async () => {
    try {
      setPrintTypeModal(true);
      setSelectedPrintOrder("bulk"); // Special flag for bulk printing
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  const handlePrintTypeSelect = async () => {
    if (!selectedPrintType) return;

    try {
      setUpdating(true);

      const orderIds =
        selectedPrintOrder === "bulk" ? selectedOrders : [selectedPrintOrder];

      const selectedOrdersData = orderIds.map((orderId) =>
        orders.find((order) => order._id === orderId)
      );
      const ordersNeedingUpdate = selectedOrdersData.filter(
        (order) =>
          !(
            order.status === "outForDelivery" ||
            (order.printedType && order.dtdcTrackingCode)
          )
      );

      if (ordersNeedingUpdate.length > 0) {
        await axiosInstance.put("/api/v1/admin/orders/update-status", {
          orders: ordersNeedingUpdate.map((order) => ({
            orderId: order._id,
            status: "printed",
          })),
        });

        await axiosInstance.put("/api/v1/admin/orders/update-printed-status", {
          orders: ordersNeedingUpdate.map((order) => ({
            orderId: order._id,
            printedType: selectedPrintType,
          })),
        });
      }

      if (selectedPrintType === "DTDC") {
        await handleDTDCPrint(orderIds);
      } else {
        const printContent = `
        <html>
          <head>
            <title>Invoice</title>
            <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
            <link href="https://cdn.jsdelivr.net/npm/remixicon@2.5.0/fonts/remixicon.css" rel="stylesheet">
            <style>
              @media print {
                @page {
                  size: 2.8in 4.8in;
                  margin: 0;
                  border: 2px solid white;
                   font-family: sans-serif;
                }
                
                body {
                  width: 2.8in;
                  /* height: 4.7in; Removed fixed height */
                  margin: 0;
                  padding: 0;
                  /* overflow: hidden; Removed overflow hidden */
                }
                 
    
                .invoice {
                  width: 2.8in;
                  /* height: 4.7in; Removed fixed height */
                  margin: 0;
                  padding: 6px;
                  box-shadow: none;
                  background: #fff;
                  page-break-inside: avoid; /* Prevent individual chunks from breaking */
                }

                .invoice.last-chunk-of-order {
                   page-break-after: always; /* Force break only after the last chunk of an order */
                }
              }

              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                font-weight: bold !important;
              }

              body {
                font-family:  Arial, sans-serif;
                line-height: 1.3;
                color: #000;
                background: #fff;
                font-size: 8px;
                font-weight: bold !important;
                overflow: hidden;
              }

              .invoice {
                background: #fff;
                margin: 5px auto;
                padding: 8px;
                max-width: 3in;
              }

              .header {
                display: flex;
                flex-direction: column;
                gap: 2px;
                margin-bottom: 6px;
                border-bottom: 1px solid #000;
                padding-bottom: 2px;
              }

              .company-name {
                font-size: 14px;
                font-weight: bold;
                letter-spacing: 0.5px;
                text-transform: uppercase;
              }

              .company-address {
                font-size: 7px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }

              .company-address-wrap {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
              }

              .invoice-details {
                text-align: left;
                margin-top: 4px;
                font-size: 8px;
              }

              .invoice-details h2 {
                font-size: 12px;
                margin-bottom: 2px;
                font-weight: bold;
              }

              .billing-barcode-row {
                gap: 6px;
                margin-bottom: 6px;
                margin-top: 6px;
                
              }
                .barcode-container{
                  display: flex;
                  flex-direction: row;
                  gap: 6px;
                }

              .address {
                flex: 1;
                border: 1px solid #000;
                border-radius: 0;
                padding: 4px;
                font-size: 8px;
              }

              .address h4 {
                margin-bottom: 3px;
                font-size: 9px;
                font-weight: bold;
              }

              .barcode-container {
                width: 50%;
                border: 1px solid #000;
                padding: 4px;
                text-align: center;
              }

              .barcode {
                width: 100%;
                height: auto;
              }

              .order-items {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 8px;
                font-size: 7px;
              }

              .order-items th {
                background: #fff;
                border: 1px solid #000;
                padding: 3px;
                text-align: left;
                font-weight: bold;
              }

              .order-items td {
                padding: 2px;
                border: 1px solid #000;
              }

              .product-cell {
                display: flex;
                align-items: center;
                gap: 4px;
              }

              .product-details {
                flex: 1;
              }

              .item-name {
                font-weight: bold;
                display: block;
                font-size: 6px;
              }

              .discount-badge {
                border: 1px solid #000;
                padding: 0 2px;
                font-size: 5px;
                display: inline-block;
              }

              .total {
                border: 1px solid #000;
                padding: 0px;
                margin-top: 6px;
                text-align: right;
                font-size: 8px;
              }

              .total div {
                margin: 1px 0;
                font-weight: bold;
              }

              .total div:last-child {
                font-weight: bold;
                font-size: 9px;
                padding-top: 3px;
                border-top: 1px solid #000;
                margin-top: 3px;
              }

              .gift-info {
                border: 1px solid #000;
                padding: 3px;
                font-size: 7px;
              }

              .gift-message {
                border: 1px solid #000;
                padding: 4px;
                margin-top: 3px;
                font-style: italic;
              }

              .payment-info {
                border: 1px solid #000;
                padding: 3px;
                margin-top: 4px;
                font-size: 7px;
                text-align: center;
              }

              .payment-info p {
                margin: 1px 0;
              }

              .color-info {
                font-size: 6px;
                display: inline-block;
                margin-left: 3px;
              }

              @media screen and (max-width: 400px) {
                .invoice {
                  padding: 6px;
                  margin: 3px;
                }
              }

              .total-row {
                display: flex;
                justify-content: space-between;
                margin: 2px 0;
                font-size: 8px;
                padding-left:4px;
                padding-right:4px;
              }

              .grand-total {
                font-weight: bold;
                font-size: 9px;
                padding-top: 3px;
                border-top: 1px solid #000;
                margin-top: 3px;
              }

              .order-items th, .order-items td {
                font-size: 7px;
                padding: 3px;
                text-align: right;
              }

              .order-items th:first-child,
              .order-items td:first-child {
                text-align: left;
              }
                .barcode-main-row{
                display: flex;
                flex-direction: row;
                gap: 6px;
                }

              td small {
                font-size: 6px;
                color: #666;
              }

              .footer-info {
                margin-top: 10px;
                font-size: 7px;
                text-align: center;
              }

              .tax-notice {
                font-size: 6px;
                font-style: italic;
                text-align: right;
                margin-top: 2px;
                color: #666;
                padding-right:4px !important;
              }

              .return-address {
                border-top: 1px dashed #000;
                margin-top: 6px;
                padding-top: 6px;
              }

              .return-address p {
                margin: 1px 0;
              }
            </style>
          </head>
          <body>
            ${selectedOrders
              .map((orderId) => {
                const order = orders.find((o) => o._id === orderId);
                const orderChunks =
                  order.orders.length > 50
                    ? Array.from(
                        { length: Math.ceil(order.orders.length / 50) },
                        (_, i) => order.orders.slice(i * 50, (i + 1) * 50)
                      )
                    : [order.orders];

                return orderChunks
                  .filter((chunk) => chunk.length > 0)
                  .map(
                    (chunk, pageIndex) => `
                    <div class="invoice ${
                      pageIndex === orderChunks.length - 1
                        ? "last-chunk-of-order"
                        : ""
                    }">
                      <div class="header">
                        <div class="invoice-details">
                          <h2>SHIPPING DETAILS</h2>
                          <div>Order #${order._id}</div>
                          <div>Date: ${formatDate(
                            order.createdAt
                          )}  GSTIN: 06ABMPD7963L1Z5</div>
                          ${
                            orderChunks.length > 1
                              ? `<div>Page ${pageIndex + 1}/${
                                  orderChunks.length
                                }</div>`
                              : ""
                          }
                        </div>
                        <div>
                          
                        </div>
                      </div>

                      ${
                        pageIndex === 0
                          ? `
                        <div class="billing-barcode-row">
                          <div class="address">
                            <h4>Bill To: ${
                              order?.shippingAddress?.name || ""
                            } (M) ${
                              order?.shippingAddress?.phone ||
                              order?.user?.phone ||
                              ""
                            }</h4>
                           <h4>Address: ${
                             order?.shippingAddress?.street || ""
                           }, ${order?.shippingAddress?.city || ""}, ${
                              order?.shippingAddress?.state || ""
                            }, ${order?.shippingAddress?.country || ""} - ${
                              order?.shippingAddress?.postalCode || ""
                            }, ${order?.shippingAddress?.email || ""}</h4>
                          </div>

                     <div class="billing-barcode-row">
                     <div class="barcode-main-row">
                     <div class="barcode-container">
                            <svg class="barcode" id="barcode-${
                              order._id
                            }-${pageIndex}"></svg>
                                
                          </div>

                          <div>
                          ${
                            order.giftPacking?.isGiftPack
                              ? `
                          <div class="gift-info">
                            <div><strong>Include gift wrap/packing material</strong></div>
                          </div>
                        `
                              : ""
                          }
                          ${
                            order.instructions
                              ? `
                          <div class="gift-info">
    <div><strong>Special Handling Instructions</strong></div>
                          </div>
                        `
                              : ""
                          } 
                          </div>
                          </div>
                        </div>
                      `
                          : ""
                      }
                          </div>

                      <table class="order-items">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Price</th>
                            <th>Qty</th>
                            <th>Subtotal</th>
                            <th>Disc</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${chunk
                            .map((item) => {
                              const subtotal = calculateItemSubtotal(
                                item.product.price,
                                item.product.discount,
                                item.quantity
                              );
                              const discountAmount = calculateItemDiscount(
                                item.product.price,
                                item.product.discount,
                                item.quantity
                              );
                              const itemTotal = subtotal - discountAmount;

                              return `
                              <tr>
                                <td>
                                  <div class="product-details">
                                    <span class="item-name">${
                                      item.product.name
                                    }</span>
                                    
                                  </div>
                                </td>
                                <td>${formatCurrency(item.product.price)}</td>
                                <td>${item.quantity}</td>
                                <td>${formatCurrency(subtotal)}</td>
                                <td>${formatCurrency(discountAmount)}</td>
                                <td>${formatCurrency(itemTotal)}</td>
                              </tr>
                            `;
                            })
                            .join("")}
                        </tbody>
                      </table>

                      ${
                        pageIndex === orderChunks.length - 1
                          ? `
                        <div class="total">
                          <div class="total-row">
                            <span>Subtotal:</span>
                            <span>${formatCurrency(
                              order.orders.reduce(
                                (acc, item) =>
                                  acc +
                                  calculateItemSubtotal(
                                    item.product.price,
                                    item.product.discount,
                                    item.quantity
                                  ),
                                0
                              )
                            )}</span>
                          </div>
                          <div class="total-row">
                            <span>Item Discount:</span>
                            <span>-${formatCurrency(
                              order.orders.reduce(
                                (acc, item) =>
                                  acc +
                                  calculateItemDiscount(
                                    item.product.price,
                                    item.product.discount,
                                    item.quantity
                                  ),
                                0
                              )
                            )}</span>
                          </div>
                          ${
                            order.couponDiscount
                              ? `
                            <div class="total-row">
                              <span>Coupon Discount:</span>
                              <span>-${formatCurrency(
                                order.couponDiscount
                              )}</span>
                            </div>
                          `
                              : ""
                          }
                          <div class="total-row">
                            <span>Shipping Charges:</span>
                            <span>${formatCurrency(
                              (() => {
                                const productTotal = order.orders.reduce(
                                  (acc, item) => {
                                    const subtotal = calculateItemSubtotal(
                                      item.product.price,
                                      item.product.discount,
                                      item.quantity
                                    );
                                    const discount = calculateItemDiscount(
                                      item.product.price,
                                      item.product.discount,
                                      item.quantity
                                    );
                                    return acc + (subtotal - discount);
                                  },
                                  0
                                );

                                const deliveryCharges = order.deliveryCharge;
                                return Math.max(0, deliveryCharges);
                              })()
                            )}</span>
                          </div>
                          ${
                            order.paymentMethod === "cod"
                              ? `<div class="total-row">
                          <span>COD Charges:</span>
                          <span>${formatCurrency(50)}</span>
                        </div>
                        `
                              : ""
                          }
                      
                          <div class="total-row grand-total">
                            <span>Total Amount:</span>
                            <span>${formatCurrency(order.totalAmount)}</span>
                          </div>
                          <div class="tax-notice">Prices are inclusive of all applicable taxes</div>
                        </div>

                  

                        <div class="payment-info">
                          <p><strong>Payment Method:</strong> ${order.paymentMethod.toUpperCase()}</p>
                          <p>Thank you for shopping with Toys Hub!</p>
                          <p><i class="ri-camera-fill"></i> Unpacking video is mandatory for any claims</p>
                        </div>

                        <div class="footer-info">
                          <div class="return-address">
                            <p>If Undelivered, please return to :</p>
                            <p>Toys Hub L-101 Model Town, 100 number Chowk, Panipat</p>
                            <p>132103, 74191-74197</p>
                          </div>
                        </div>
                      `
                          : ""
                      }
                    </div>
                  `
                  )
                  .join("");
              })
              .join("")}
            <script>
              window.onload = function() {
                ${selectedOrders
                  .map((orderId) => {
                    const order = orders.find((o) => o._id === orderId);
                    return `
                    JsBarcode("#barcode-${order._id}-0", "${order._id}", {
                      format: "CODE128",
                      width: 2.5,
                      height: 50,
                      displayValue: true,
                      fontSize: 35,
                      margin: 4
                    });
                  `;
                  })
                  .join("")}
              }
            </script>
          </body>
        </html>
      `;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
          setTimeout(() => {
            window.location.reload();
          }, 250);
        }, 250);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
    } finally {
      setUpdating(false);
      setPrintTypeModal(false);
      setSelectedPrintOrder(null);
      setSelectedPrintType("");
    }
  };

  const handleDTDCPrint = async (orderIds) => {
    try {
      const dtdcData = {
        consignments: [],
      };
      const myHeaders = new Headers();
      myHeaders.append("api-key", "8face80a07169499e46445b5bd3c7e");
      myHeaders.append("Content-Type", "application/json");
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        redirect: "follow",
      };
      const referenceNumbers = [];

      // Build the array of consignments - one per order, not per product
      for (const orderId of orderIds) {
        const order = orders.find((o) => o._id === orderId);
        if (!order) {
          console.error(`Order with ID ${orderId} not found`);
          continue;
        }

        // If the order already has a tracking code, use it
        if (order.dtdcTrackingCode) {
          referenceNumbers.push(order.dtdcTrackingCode);
          continue;
        }

        // Calculate the total weight for all products in the order
        const totalWeight = Math.ceil(
          order.orders.reduce(
            (acc, item) =>
              acc + (item.product.volumetricWeight * item.quantity || 0),
            0
          ) || 1
        );

        const serviceTypeId =
          totalWeight > 10 ? "B2C GROUND ECONOMY" : "B2C SMART EXPRESS";

        // Create product description from all products
        const description = order.orders
          .map((item) => `${item.product.name} (${item.quantity})`)
          .join(", ")
          .substring(0, 100); // Keep description under reasonable length

        // Create just one consignment per order
        const consignment = {
          customer_code: "JO1691",
          service_type_id: serviceTypeId,
          load_type: "NON-DOCUMENT",
          consignment_type: "Forward",
          dimension_unit: "cm",
          length: "1",
          width: "2",
          height: "34",
          weight_unit: "kg",
          weight: totalWeight.toFixed(2),
          declared_value: order.totalAmount.toFixed(2),
          eway_bill: "",
          invoice_number: "",
          invoice_date: new Date(),
          num_pieces: 1,
          origin_details: {
            name: "Toys Hub",
            phone: "7419174197",
            alternate_phone: "7419174197",
            address_line_1:
              "Toys Hub L-101 Model Town, 100 number Chowk, Panipat - 132103",
            address_line_2: "",
            pincode: "132103",
            city: "Panipat",
            state: "Haryana",
          },
          destination_details: {
            name: order?.shippingAddress?.name,
            phone: order?.shippingAddress?.phone || order?.user?.phone,
            alternate_phone:
              order?.shippingAddress?.phone || order?.user?.phone,
            address_line_1: order?.shippingAddress?.street,
            address_line_2: "",
            pincode: order?.shippingAddress?.postalCode,
            city: order?.shippingAddress?.city,
            state: order?.shippingAddress?.state,
          },
          return_details: {
            name: "Toys Hub",
            phone: "7419174197",
            alternate_phone: "7419174197",
            address_line_1:
              "Toys Hub L-101 Model Town, 100 number Chowk, Panipat - 132103",
            address_line_2: "",
            pincode: "132103",
            city: "Panipat",
            state: "Haryana",
            country: "India",
            email: "",
          },
          customer_reference_number: `SO-${order._id}`,
          cod_collection_mode: order.paymentMethod === "cod" ? "cash" : "",
          cod_amount: order.paymentMethod === "cod" ? order.totalAmount : "",
          commodity_id: "2",
          description: description,
          reference_number: "",
        };

        dtdcData.consignments.push(consignment);
      }

      // Continue with the rest of the function (making API calls, etc.)
      if (dtdcData.consignments.length === 0) {
        referenceNumbers.push(
          ...orderIds.map((orderId) => {
            const order = orders.find((o) => o._id === orderId);
            return order.dtdcTrackingCode;
          })
        );
      } else {
        const raw = JSON.stringify(dtdcData);
        console.log("raw", raw);
        requestOptions.body = raw;

        const softDataResponse = await fetch(
          "https://dtdcapi.shipsy.io/api/customer/integration/consignment/softdata",
          requestOptions
        );
        const softDataResult = await softDataResponse.json();
        if (
          softDataResult &&
          softDataResult.status === "OK" &&
          softDataResult.data
        ) {
          console.log("softDataResult.data", softDataResult.data);
          for (const result of softDataResult.data) {
            referenceNumbers.push(result.reference_number);
            const matchingOrder = orders.find(
              (order) =>
                order._id == result.customer_reference_number.replace("SO-", "")
            );
            if (matchingOrder) {
              try {
                await axiosInstance.put(
                  "/api/v1/admin/orders/update-dtdc-tracking-code",
                  {
                    orderId: matchingOrder._id,
                    dtdcTrackingCode: result.reference_number,
                  }
                );
              } catch (updateError) {
                console.error(
                  "Error updating DTDC tracking code:",
                  updateError
                );
              }
            }
          }
        } else {
          console.error("Soft data API call failed:", softDataResult);
          return;
        }
      }
      console.log("referenceNumbers", referenceNumbers);
      if (referenceNumbers.length > 0) {
        const printContent = `
        <html>
          <head>
            <title>Invoice</title>
            <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
            <link href="https://cdn.jsdelivr.net/npm/remixicon@2.5.0/fonts/remixicon.css" rel="stylesheet">
            <style>
              @media print {
                @page {
                  size: 2.8in 4.8in;
                  margin: 0;
                  border: 2px solid white;
                  font-family: sans-serif;
                }
                
                body {
                  width: 2.8in;
                   /* height: 4.7in; Removed fixed height */
                  margin: 0;
                  padding: 0;
                   /* overflow: hidden; Removed overflow hidden */
                }
                 
    
                .invoice {
                  width: 2.8in;
                   /* height: 4.7in; Removed fixed height */
                  margin: 0;
                  padding: 6px;
                  box-shadow: none;
                  background: #fff;
                  page-break-inside: avoid; /* Prevent individual chunks from breaking */
                }

                 .invoice.last-chunk-of-order {
                   page-break-after: always; /* Force break only after the last chunk of an order */
                }
              }

              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                font-weight: bold !important;
              }

              body {
                font-family:  Arial, sans-serif;
                line-height: 1.3;
                color: #000;
                background: #fff;
                font-size: 8px;
                font-weight: bold !important;
                overflow: hidden;
              }

              .invoice {
                background: #fff;
                margin: 5px auto;
                padding: 8px;
                max-width: 3in;
              }

              .header {
                display: flex;
                flex-direction: column;
                gap: 2px;
                margin-bottom: 6px;
                border-bottom: 1px solid #000;
                padding-bottom: 2px;
              }

               .barcode-main-row{
                display: flex;
                flex-direction: row;
                gap: 6px;
                }

              .company-name {
                font-size: 14px;
                font-weight: bold;
                letter-spacing: 0.5px;
                text-transform: uppercase;
              }

              .company-address {
                font-size: 7px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }

              .company-address-wrap {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
              }

              .invoice-details {
                text-align: left;
                margin-top: 4px;
                font-size: 8px;
              }

              .invoice-details h2 {
                font-size: 12px;
                margin-bottom: 2px;
                font-weight: bold;
              }

              .billing-barcode-row {
                gap: 6px;
                margin-bottom: 6px;
                margin-top: 6px;
              }

              .billing-barcode-row11 {
                display:flex;
              }

              .address {
                flex: 1;
                border: 1px solid #000;
                border-radius: 0;
                padding: 4px;
                font-size: 8px;
              }

              .address h4 {
                margin-bottom: 3px;
                font-size: 9px;
                font-weight: bold;
              }

              .barcode-container {
                width: 50%;
                border: 1px solid #000;
                padding: 4px;
                text-align: center;
              }

              .barcode {
                width: 100%;
                height: auto;
              }

              .order-items {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 8px;
                font-size: 7px;
              }

              .order-items th {
                background: #fff;
                border: 1px solid #000;
                padding: 3px;
                text-align: left;
                font-weight: bold;
              }

              .order-items td {
                padding: 2px;
                border: 1px solid #000;
              }

              .product-cell {
                display: flex;
                align-items: center;
                gap: 4px;
              }

              .product-details {
                flex: 1;
              }

              .item-name {
                font-weight: bold;
                display: block;
                font-size: 6px;
              }

              .discount-badge {
                border: 1px solid #000;
                padding: 0 2px;
                font-size: 5px;
                display: inline-block;
              }

              .total {
                border: 1px solid #000;
                padding: 0px !important;
                margin-top: 6px;
                text-align: right;
                font-size: 8px;
              }

              .total div {
                margin: 1px 0;
                font-weight: bold;
              }

              .total div:last-child {
                font-weight: bold;
                font-size: 9px;
                padding-top: 3px;
                border-top: 1px solid #000;
                margin-top: 3px;
              }

              .gift-info {
                border: 1px solid #000;
                padding: 3px;
                font-size: 7px;
              }

              .gift-message {
                border: 1px solid #000;
                padding: 4px;
                margin-top: 3px;
                font-style: italic;
              }

              .payment-info {
                border: 1px solid #000;
                padding: 3px;
                margin-top: 4px;
                font-size: 7px;
                text-align: center;
              }

              .payment-info p {
                margin: 1px 0;
              }

              .color-info {
                font-size: 6px;
                display: inline-block;
                margin-left: 3px;
              }

              @media screen and (max-width: 400px) {
                .invoice {
                  padding: 6px;
                  margin: 3px;
                }
              }

              .total-row {
                display: flex;
                justify-content: space-between;
                margin: 2px 0;
                font-size: 8px;
                padding-left:4px;
                padding-right:4px;
              }

              .grand-total {
                font-weight: bold;
                font-size: 9px;
                padding-top: 3px;
                border-top: 1px solid #000;
                margin-top: 3px;
              }

              .order-items th, .order-items td {
                font-size: 7px;
                padding: 3px;
                text-align: right;
              }

              .order-items th:first-child,
              .order-items td:first-child {
                text-align: left;
              }

              td small {
                font-size: 6px;
                color: #666;
              }

              .footer-info {
                margin-top: 10px;
                font-size: 7px;
                text-align: center;
              }
                .bill-flex{
                display:flex;
                }

              .tax-notice {
                font-size: 6px;
                font-style: italic;
                text-align: right;
                margin-top: 2px;
                color: #666;
                padding-right:4px !important;
              }

              .return-address {
                border-top: 1px dashed #000;
                margin-top: 6px;
                padding-top: 6px;
              }

              .return-address p {
                margin: 1px 0;
              }
            </style>
          </head>
          <body>
            ${orderIds
              .map((orderId) => {
                const order = orders.find((o) => o._id === orderId);
                const orderChunks =
                  order.orders.length > 50
                    ? Array.from(
                        { length: Math.ceil(order.orders.length / 50) },
                        (_, i) => order.orders.slice(i * 50, (i + 1) * 50)
                      )
                    : [order.orders];

                return orderChunks
                  .filter((chunk) => chunk.length > 0)
                  .map((chunk, pageIndex) => {
                    // Calculate the index of the reference number
                    const refIndex =
                      orderIds.indexOf(orderId) * orderChunks.length +
                      pageIndex;
                    const referenceNumber = referenceNumbers[refIndex];
                    console.log("referenceNumber", referenceNumber);
                    return `
                    <div class="invoice ${
                      pageIndex === orderChunks.length - 1
                        ? "last-chunk-of-order"
                        : ""
                    }">
                      <div class="header">
                        <div class="invoice-details">
                          <h2>SHIPPING DETAILS</h2>
                          <div>Order #${order._id}</div>
                          <div>Date: ${formatDate(
                            order.createdAt
                          )}  GSTIN: 06ABMPD7963L1Z5</div>
                          ${
                            orderChunks.length > 1
                              ? `<div>Page ${pageIndex + 1}/${
                                  orderChunks.length
                                }</div>`
                              : ""
                          }
                        </div>
                        <div>

                        </div>
                      </div>

                      ${
                        pageIndex === 0
                          ? `
                        <div class="billing-barcode-row">
                         <div class="address">
                            <h4>Bill To: ${
                              order?.shippingAddress?.name || ""
                            } (M) ${
                              order?.shippingAddress?.phone ||
                              order?.user?.phone ||
                              ""
                            }</h4>
                           <h4>Address: ${
                             order?.shippingAddress?.street || ""
                           }, ${order?.shippingAddress?.city || ""}, ${
                              order?.shippingAddress?.state || ""
                            }, ${order?.shippingAddress?.country || ""} - ${
                              order?.shippingAddress?.postalCode || ""
                            }, ${order?.shippingAddress?.email || ""}</h4>
                          </div>
                          <div class="bill-flex">
                          <div class="billing-barcode-row">
                          <div class="barcode-main-row">
<div class="barcode-container">
                            <svg class="barcode" id="barcode-${referenceNumber}"></svg>

                          </div>
                          <div>
                          ${
                            order.giftPacking?.isGiftPack
                              ? `
                          <div class="gift-info">
                            <div><strong>Include gift wrap/packing material</strong></div>
                          </div>
                        `
                              : ""
                          }
                          ${
                            order.instructions
                              ? `
                          <div class="gift-info">
                            <div><strong>Special Handling Instructions</strong></div>
                          </div>
                        `
                              : ""
                          }
                        </div>
                          </div>
                          </div>
                        </div>
                      `
                          : ""
                      }
                          </div>

                      <table class="order-items">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Price</th>
                            <th>Qty</th>
                            <th>Subtotal</th>
                            <th>Disc</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${chunk
                            .map((item) => {
                              const subtotal = calculateItemSubtotal(
                                item.product.price,
                                item.product.discount,
                                item.quantity
                              );
                              const discountAmount = calculateItemDiscount(
                                item.product.price,
                                item.product.discount,
                                item.quantity
                              );
                              const itemTotal = subtotal - discountAmount;

                              return `
                                <tr>
                                  <td>
                                    <div class="product-details">
                                      <span class="item-name">${
                                        item.product.name
                                      }</span>

                                    </div>
                                  </td>
                                  <td>${formatCurrency(item.product.price)}</td>
                                  <td>${item.quantity}</td>
                                  <td>${formatCurrency(subtotal)}</td>
                                  <td>${formatCurrency(discountAmount)}</td>
                                  <td>${formatCurrency(itemTotal)}</td>
                                </tr>
                              `;
                            })
                            .join("")}
                        </tbody>
                      </table>

                      ${
                        pageIndex === orderChunks.length - 1
                          ? `
                        <div class="total">
                          <div class="total-row">
                            <span>Subtotal:</span>
                            <span>${formatCurrency(
                              order.orders.reduce(
                                (acc, item) =>
                                  acc +
                                  calculateItemSubtotal(
                                    item.product.price,
                                    item.product.discount,
                                    item.quantity
                                  ),
                                0
                              )
                            )}</span>
                          </div>
                          <div class="total-row">
                            <span>Item Discount:</span>
                            <span>-${formatCurrency(
                              order.orders.reduce(
                                (acc, item) =>
                                  acc +
                                  calculateItemDiscount(
                                    item.product.price,
                                    item.product.discount,
                                    item.quantity
                                  ),
                                0
                              )
                            )}</span>
                          </div>
                          ${
                            order.couponDiscount
                              ? `
                            <div class="total-row">
                              <span>Coupon Discount:</span>
                              <span>-${formatCurrency(
                                order.couponDiscount
                              )}</span>
                            </div>
                          `
                              : ""
                          }
                          <div class="total-row">
                            <span>Shipping Charges:</span>
                            <span>${formatCurrency(
                              (() => {
                                const productTotal = order.orders.reduce(
                                  (acc, item) => {
                                    const subtotal = calculateItemSubtotal(
                                      item.product.price,
                                      item.product.discount,
                                      item.quantity
                                    );
                                    const discount = calculateItemDiscount(
                                      item.product.price,
                                      item.product.discount,
                                      item.quantity
                                    );
                                    return acc + (subtotal - discount);
                                  },
                                  0
                                );

                                const deliveryCharges = order.deliveryCharge;
                                return Math.max(0, deliveryCharges);
                              })()
                            )}</span>
                          </div>
                          ${
                            order.paymentMethod === "cod"
                              ? `<div class="total-row">
                            <span>COD Charges:</span>
                            <span>${formatCurrency(50)}</span>
                          </div>
                          `
                              : ""
                          }
                          <div class="total-row grand-total">
                            <span>Total Amount:</span>
                            <span>${formatCurrency(order.totalAmount)}</span>
                          </div>
                          <div class="tax-notice">Prices are inclusive of all applicable taxes</div>
                        </div>


                        <div class="payment-info">
                          <p><strong>Payment Method:</strong> ${order.paymentMethod.toUpperCase()}</p>
                          <p>Thank you for shopping with Toys Hub!</p>
                          <p><i class="ri-camera-fill"></i> Unpacking video is mandatory for any claims</p>
                        </div>

                        <div class="footer-info">
                          <div class="return-address">
                            <p>If Undelivered, please return to :</p>
                            <p>Toys Hub L-101 Model Town, 100 number Chowk, Panipat</p>
                            <p>132103, 74191-74197</p>
                          </div>
                        </div>
                      `
                          : ""
                      }
                    </div>
                  `;
                  })
                  .join("");
              })
              .join("")}
            <script>
              window.onload = function() {
                ${referenceNumbers
                  .map((referenceNumber) => {
                    return `
                    JsBarcode("#barcode-${referenceNumber}", "${referenceNumber}", {
                      format: "CODE128",
                      width: 2.5,
                      height: 50,
                      displayValue: true,
                      fontSize: 35,
                      margin: 4
                    });
                  `;
                  })
                  .join("")}
              }
            </script>
          </body>
        </html>
      `;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
          setTimeout(() => {
            window.location.reload();
          }, 250);
        }, 250);
      } else {
        console.error("No reference numbers obtained for printing.");
      }
    } catch (error) {
      console.error("Error in DTDC Print:", error);
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const dateFilterDropdown = (
    <UncontrolledDropdown>
      <DropdownToggle
        caret
        color="primary"
        outline
        className="px-4 d-flex align-items-center"
      >
        <i className="ri-calendar-line me-2"></i>
        {customDate
          ? new Date(customDate).toLocaleDateString()
          : dateFilter
          ? dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)
          : "All Time"}
      </DropdownToggle>
      <DropdownMenu className="shadow-lg">
        <DropdownItem
          active={!dateFilter && !customDate}
          onClick={() => {
            setDateFilter("");
            setCustomDate("");
          }}
          className="d-flex align-items-center"
        >
          <i className="ri-time-line me-2"></i>
          All Time
        </DropdownItem>
        <DropdownItem
          active={dateFilter === "today"}
          onClick={() => {
            setDateFilter("today");
            setCustomDate("");
          }}
          className="d-flex align-items-center"
        >
          <i className="ri-calendar-todo-line me-2"></i>
          Today
        </DropdownItem>
        <DropdownItem
          active={dateFilter === "yesterday"}
          onClick={() => {
            setDateFilter("yesterday");
            setCustomDate("");
          }}
          className="d-flex align-items-center"
        >
          <i className="ri-calendar-todo-line me-2"></i>
          Yesterday
        </DropdownItem>
        <DropdownItem
          active={dateFilter === "week"}
          onClick={() => {
            setDateFilter("week");
            setCustomDate("");
          }}
          className="d-flex align-items-center"
        >
          <i className="ri-calendar-todo-line me-2"></i>
          Last 7 Days
        </DropdownItem>
        <DropdownItem
          active={dateFilter === "month"}
          onClick={() => {
            setDateFilter("month");
            setCustomDate("");
          }}
          className="d-flex align-items-center"
        >
          <i className="ri-calendar-todo-line me-2"></i>
          Last 30 Days
        </DropdownItem>
        <DropdownItem divider />
        <div className="px-3 py-2">
          <div className="mb-2">Select Specific Date</div>
          <Input
            type="date"
            value={customDate}
            onChange={(e) => {
              setCustomDate(e.target.value);
              setDateFilter("");
            }}
            className="form-control-sm"
          />
        </div>
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  const styles = `
    .badge .badge {
      vertical-align: middle;
    }
    
    .dropdown-item {
      padding: 8px 16px;
    }
    
    .dropdown-item i {
      font-size: 1.1em;
    }
    
    .dropdown-item:hover {
      background-color: #f8f9fa;
    }
    
    .dropdown-item.active {
      background-color: #eef2ff;
      color: #556ee6;
    }
  `;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Orders"
          breadcrumbItems={[
            { title: "Ecommerce", link: "#" },
            { title: "Orders", link: "#" },
          ]}
        />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap gap-3 align-items-center mb-4">
                  {dateFilterDropdown}

                  {selectedOrders.length > 0 && (
                    <div className="action-buttons d-flex gap-2">
                      <UncontrolledDropdown>
                        <DropdownToggle
                          caret
                          color="success"
                          className="px-4 d-flex align-items-center"
                        >
                          <i className="ri-edit-2-line me-2"></i>
                          Bulk Update{" "}
                          {updating && <Spinner size="sm" className="ms-2" />}
                        </DropdownToggle>
                        <DropdownMenu className="shadow-lg">
                          <DropdownItem
                            onClick={() => setBulkStatus("pending")}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-time-line me-2 text-warning"></i>
                            Pending
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => setBulkStatus("accepted")}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-time-line me-2 text-success"></i>
                            Accepted
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => setBulkStatus("printed")}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-time-line me-2 text-info"></i>
                            Printed
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => setBulkStatus("outForDelivery")}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-time-line me-2 text-primary"></i>
                            Dispatched
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => setBulkStatus("delivered")}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-time-line me-2 text-success"></i>
                            Delivered
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => setBulkStatus("rejected")}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-time-line me-2 text-danger"></i>
                            Rejected
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => setBulkStatus("cancelled")}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-time-line me-2 text-danger"></i>
                            Cancelled
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => setBulkStatus("paymentpending")}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-time-line me-2 text-danger"></i>
                            Payment Pending
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>

                      <Button
                        color="success"
                        disabled={!bulkStatus || updating}
                        onClick={handleBulkStatusUpdate}
                        className="px-4 d-flex align-items-center"
                      >
                        <i className="ri-check-line me-2"></i>
                        Apply
                      </Button>

                      <Button
                        color="info"
                        onClick={handlePrintOrders}
                        className="px-4 d-flex align-items-center"
                      >
                        <i className="ri-printer-line me-2"></i>
                        Print Orders
                      </Button>
                    </div>
                  )}

                  <div className="filters-group d-flex gap-2">
                    <UncontrolledDropdown>
                      <DropdownToggle
                        caret
                        color="primary"
                        outline
                        className="px-4 d-flex align-items-center"
                      >
                        <i className="ri-filter-3-line me-2"></i>
                        {statusFilter
                          ? statusFilter === "outForDelivery" ||
                            statusFilter === "outfordelivery"
                            ? "Dispatched"
                            : statusFilter === "paymentpending"
                            ? "Payment Pending"
                            : statusFilter
                          : "All Status"}
                      </DropdownToggle>
                      <DropdownMenu className="shadow-lg">
                        <DropdownItem
                          active={statusFilter === ""}
                          onClick={() => setStatusFilter("")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-apps-line me-2"></i>
                          All Status
                        </DropdownItem>
                        <DropdownItem divider />
                        <DropdownItem
                          active={statusFilter === "pending"}
                          onClick={() => setStatusFilter("pending")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-time-line me-2 text-warning"></i>
                          Pending
                        </DropdownItem>
                        <DropdownItem
                          active={statusFilter === "accepted"}
                          onClick={() => setStatusFilter("accepted")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-time-line me-2 text-success"></i>
                          Accepted
                        </DropdownItem>
                        <DropdownItem
                          active={statusFilter === "printed"}
                          onClick={() => setStatusFilter("printed")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-time-line me-2 text-info"></i>
                          Printed
                        </DropdownItem>
                        <DropdownItem
                          active={statusFilter === "outForDelivery"}
                          onClick={() => setStatusFilter("outForDelivery")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-time-line me-2 text-primary"></i>
                          Dispatched
                        </DropdownItem>
                        <DropdownItem
                          active={statusFilter === "delivered"}
                          onClick={() => setStatusFilter("delivered")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-time-line me-2 text-success"></i>
                          Delivered
                        </DropdownItem>
                        <DropdownItem
                          active={statusFilter === "rejected"}
                          onClick={() => setStatusFilter("rejected")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-time-line me-2 text-danger"></i>
                          Rejected
                        </DropdownItem>
                        <DropdownItem
                          active={statusFilter === "cancelled"}
                          onClick={() => setStatusFilter("cancelled")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-time-line me-2 text-danger"></i>
                          Cancelled
                        </DropdownItem>
                        <DropdownItem
                          active={statusFilter === "paymentpending"}
                          onClick={() => setStatusFilter("paymentpending")}
                          className="d-flex align-items-center"
                        >
                          <i className="ri-time-line me-2 text-danger"></i>
                          Payment Pending
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>

                  <div className="search-box ms-auto d-flex gap-2 align-items-center">
                    <div className="position-relative">
                      <Input
                        type="text"
                        className="form-control rounded-pill px-4 py-2"
                        style={{ minWidth: "200px", paddingLeft: "15px" }}
                        placeholder="Search by ID..."
                        value={searchById}
                        onChange={handleSearchById}
                      />
                      {searchById && (
                        <Button
                          color="link"
                          className="position-absolute"
                          style={{
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            padding: "0",
                            color: "#6c757d",
                            zIndex: 10,
                          }}
                          onClick={clearIdSearch}
                        >
                          <i className="ri-close-line"></i>
                        </Button>
                      )}
                    </div>
                    <div className="position-relative">
                      <Input
                        type="text"
                        className="form-control rounded-pill px-4 py-2"
                        style={{ minWidth: "200px", paddingLeft: "15px" }}
                        placeholder="Search orders..."
                        value={searchQuery}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  {initialLoading ? (
                    <div className="text-center my-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <>
                      {refreshing && (
                        <div className="text-center my-2">
                          <Spinner size="sm" color="primary" />
                        </div>
                      )}
                      <OrdersTable
                        orders={orders || []}
                        selectedOrders={selectedOrders || []}
                        setSelectedOrders={setSelectedOrders}
                        handleCheckboxChange={(id) => {
                          if (selectedOrders.includes(id)) {
                            setSelectedOrders(
                              selectedOrders.filter((orderId) => orderId !== id)
                            );
                          } else {
                            setSelectedOrders([...selectedOrders, id]);
                          }
                        }}
                        initialLoading={initialLoading || false}
                        refreshing={refreshing || false}
                        pagination={{
                          page: pagination.page,
                          limit: pagination.limit,
                          totalOrders: pagination.totalOrders,
                        }}
                        onPageChange={handlePageChange}
                        handleStatusUpdate={handleSingleStatusUpdate}
                      />

                      <div className="d-flex align-items-center justify-content-end mt-4 pe-3">
                        <div className="d-flex align-items-center">
                          <span className="me-2 text-muted">Show:</span>
                          <UncontrolledDropdown>
                            <DropdownToggle
                              caret
                              color="light"
                              outline
                              className="px-3 d-flex align-items-center shadow-none"
                              style={{
                                borderRadius: "6px",
                                minWidth: "90px",
                              }}
                            >
                              {pagination.limit} Rows
                            </DropdownToggle>
                            <DropdownMenu
                              className="shadow-lg"
                              style={{ minWidth: "90px" }}
                            >
                              {[10, 25, 50, 100].map((pageSize) => (
                                <DropdownItem
                                  key={pageSize}
                                  onClick={() =>
                                    setPagination((prev) => ({
                                      ...prev,
                                      limit: pageSize,
                                      page: 1,
                                    }))
                                  }
                                  active={pagination.limit === pageSize}
                                  className="d-flex align-items-center justify-content-center"
                                >
                                  {pageSize} Rows
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                          <span className="ms-3 text-muted">
                            Showing{" "}
                            {(pagination.page - 1) * pagination.limit + 1} to{" "}
                            {Math.min(
                              pagination.page * pagination.limit,
                              pagination.totalOrders
                            )}{" "}
                            of {pagination.totalOrders} entries
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Print Type Selection Modal */}
      <Modal
        isOpen={printTypeModal}
        toggle={() => {
          setPrintTypeModal(false);
          setSelectedPrintOrder(null);
          setSelectedPrintType("");
        }}
      >
        <ModalHeader toggle={() => setPrintTypeModal(false)}>
          Select Print Type
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="d-block mb-3">Choose Print Type:</Label>
            <div className="d-flex gap-3">
              <div className="form-check">
                <input
                  type="radio"
                  className="form-check-input"
                  id="printTypeDTDC"
                  name="printType"
                  value="DTDC"
                  checked={selectedPrintType === "DTDC"}
                  onChange={(e) => setSelectedPrintType(e.target.value)}
                />
                <label className="form-check-label" htmlFor="printTypeDTDC">
                  DTDC
                </label>
              </div>
              <div className="form-check">
                <input
                  type="radio"
                  className="form-check-input"
                  id="printTypeToyshub"
                  name="printType"
                  value="Toyshub"
                  checked={selectedPrintType === "Toyshub"}
                  onChange={(e) => setSelectedPrintType(e.target.value)}
                />
                <label className="form-check-label" htmlFor="printTypeToyshub">
                  Toyshub
                </label>
              </div>
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => {
              setPrintTypeModal(false);
              setSelectedPrintOrder(null);
              setSelectedPrintType("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handlePrintTypeSelect}
            disabled={!selectedPrintType || updating}
          >
            {updating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Confirm & Print"
            )}
          </Button>
        </ModalFooter>
      </Modal>
      <style>{styles}</style>
    </div>
  );
};

export default EcommerceOrders;
